'use client';

import {coreKeeperAppReady} from '@mail-core/dashboard';
import {
  COOKIE_TMR_LVID,
  COOKIE_YM_UID,
  getCookie,
  getUserIpFromCookieInWebCatalog,
} from '@utils/cookies';
import {generateMyTrackerPixel} from '@utils/pixel';
import {useTracePathname} from '@utils/tracer/useTracePathname';
import {getEnvConfig} from '@web-catalog/env';
import {usePathname, useSearchParams} from 'next/navigation';
import {useEffect, useRef} from 'react';

import {
  emitTmrEvent,
  emitYmEvent,
  enableTmrDebugging,
} from '@web-catalog/utils/analytics';

import {Props} from './types';

export const ScriptsInitialization = ({userIp, envConfig}: Props) => {
  const searchParams = useSearchParams();
  const path = usePathname();

  const {YA_METRIKA_ID} = envConfig;

  useEffect(() => {
    generateMyTrackerPixel({
      cookieDomain: envConfig.COOKIE_DOMAIN,
      userIp: getUserIpFromCookieInWebCatalog() || userIp || '',
      serviceVersion: `${process.env.APP_VERSION}`,
      appDeeplinkBaseUrl: getEnvConfig().DEEPLINK_BASE,
      emitPixelEvents: (payload) => {
        emitTmrEvent({
          eventName: 'mt_pixel',
          params: {
            ...payload,
            metrikaUid: getCookie(COOKIE_YM_UID) ?? '',
            pageURL: document.location.href.split('?')[0],
          },
        });

        emitYmEvent({eventName: 'mt_pixel', params: payload});
      },
    });
  }, [envConfig, searchParams, userIp]);

  useEffect(() => {
    if (searchParams?.has('_tmr_debug')) {
      enableTmrDebugging();
    }

    coreKeeperAppReady();

    window.xray?.addMiddleware((t, params, gaTrackingId) => {
      if (t === 'pk-err') {
        const error = params?.rlog_message?.err;

        const err = new Error();
        err.name = 'XRAY-PK-ERR';
        err.message = `Ошибка xray-pk-err - ${error?.url}`;
      }

      return {t, params, gaTrackingId};
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ymUserParamsInitedRef = useRef(false);

  useEffect(() => {
    if (ymUserParamsInitedRef.current) {
      return;
    }

    const initYmUserParams = () => {
      const tmrCookie = getCookie(COOKIE_TMR_LVID);

      if (tmrCookie && window.ym) {
        ymUserParamsInitedRef.current = true;

        window.ym(YA_METRIKA_ID, 'userParams', {
          tmr_lvid: tmrCookie,
        });

        return true;
      }
    };

    if (initYmUserParams()) {
      return;
    }

    let intervalHandle: NodeJS.Timeout | null = setInterval(() => {
      if (initYmUserParams()) {
        if (intervalHandle) {
          clearInterval(intervalHandle);
          intervalHandle = null;
        }
      }
    }, 1);

    return () => {
      if (intervalHandle) {
        clearInterval(intervalHandle);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.ym) {
      window.ym(YA_METRIKA_ID, 'hit', document.location.href);
    }
  }, [path, YA_METRIKA_ID]);

  useTracePathname();

  return null;
};
